import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, inject } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import type { Theme } from '../../shared-services/theme.types';
import {
  buildConfigurableAxisLabel,
  formatTooltipValue,
  getTooltipLabel,
  TooltipFieldConfig,
} from '../../utils/chart.utils';

type ComboSeriesMode = 'bar' | 'line';
type LineStrokeStyle = 'solid' | 'dashed' | 'dotted';
type LabelPosition =
  | 'top'
  | 'inside'
  | 'insideTop'
  | 'insideBottom'
  | 'insideRight'
  | 'insideLeft'
  | 'bottom';

type LabelOverflow = 'truncate' | 'break' | 'breakAll';

interface ComboSeriesConfig {
  mode: ComboSeriesMode;
  name: string;
  valueField: string;
  dimensionField?: string;
  dimensionValue?: string | number;
  yAxisIndex?: number;
  color?: string;
  prefix?: string;
  valueSuffix?: string;
  valueDecimals?: number;
  valueScale?: number;
  showLabel?: boolean;
  labelPosition?: LabelPosition;
  labelDistance?: number;
  labelOffset?: [number, number];
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number | string;
  labelLayout?: any;
  labelBandRow?: number;
  stack?: string;
  barWidth?: string | number;
  barMaxWidth?: string | number;
  barMinHeight?: number;
  lineStyleType?: LineStrokeStyle;
  lineWidth?: number;
  lineSmooth?: boolean | number;
  lineSymbol?: string;
  lineSymbolSize?: number;
  showSymbol?: boolean;
  connectNulls?: boolean;
  sampling?: 'lttb' | 'average' | 'max' | 'min' | 'sum';
  hidden?: boolean;
  visible?: boolean;
  initiallyHidden?: boolean;
}

interface ComboYAxisConfig {
  name?: string;
  position?: 'left' | 'right';
  min?: number;
  max?: number;
  interval?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  useFormatStat?: boolean;
  show?: boolean;
  splitLine?: boolean;
  splitLineColor?: string;
  splitLineType?: LineStrokeStyle;
  labelColor?: string;
  labelFontSize?: number;
  nameGap?: number;
  nameColor?: string;
  nameFontSize?: number;
}

interface ComboRegionAnnotation {
  name?: string;
  startCategory: string;
  endCategory: string;
  color?: string;
  label?: string;
  targetSeries?: string;
}

interface ComboLineAnnotation {
  name?: string;
  axis?: 'x' | 'y';
  category?: string;
  value: number | string;
  color?: string;
  style?: LineStrokeStyle;
  width?: number;
  label?: string;
  targetSeries?: string;
}

interface ComboPointAnnotation {
  name?: string;
  category: string;
  value?: number;
  color?: string;
  symbol?: string;
  size?: number;
  label?: string;
  targetSeries?: string;
}

interface ComboAnnotationsConfig {
  regions?: ComboRegionAnnotation[];
  lines?: ComboLineAnnotation[];
  points?: ComboPointAnnotation[];
}

interface TooltipSeriesMeta {
  mode: ComboSeriesMode;
  prefix: string;
  suffix: string;
  decimals: number;
  scale: number;
}

interface CategoryMetaInfo {
  quarter?: string;
  year?: string;
}

interface NormalizedComboData {
  categories: string[];
  rowsByCategory: Record<string, any[]>;
  primaryRowByCategory: Record<string, any>;
  categoryMetaByCategory: Record<string, CategoryMetaInfo>;
  metaInfo: {
    totalRecords?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  };
}

interface SeriesAnnotationBundle {
  markAreaData?: any[];
  markLineData?: any[];
  markPointData?: any[];
}

@Component({
  selector: 'app-multi-series-bar-line-combo-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multi-series-bar-line-combo-chart.component.html',
  styleUrl: './multi-series-bar-line-combo-chart.component.scss',
})
export class MultiSeriesBarLineComboChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  metaInfo: {
    totalRecords?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  } = {};

  protected myChart!: echarts.ECharts;
  protected allCategories: string[] = [];
  protected allSeriesNames: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private selectedLegends: string[] = [];
  private lastRawPayload: any;
  private currentTheme?: Theme;
  private readonly handleResize = () => this.myChart?.resize();
  private readonly fallbackPalette = ['#2563eb', '#0f766e', '#94a3b8', '#f97316', '#7c3aed', '#dc2626', '#0ea5e9', '#475569'];

  ngAfterViewInit(): void {
    if (this.data && this.data.enableLegendClick === undefined) {
      this.data.enableLegendClick = true;
    }

    this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
      onHighlightCategories: (categories) => this.highlightCategories(categories),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
      onSelectLegendsByIndices: (indices) => this.applyLegendSelectionFromWrapper(indices),
    });

    this.loadAndRenderChart();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart) {
        return;
      }
      const chartDom = document.getElementById(this.data?.widgetId || '');
      if (!chartDom) {
        return;
      }
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      if (this.lastRawPayload !== undefined) {
        this.renderChart(this.lastRawPayload);
        return;
      }
      this.showNoDataMessage();
    });

    window.addEventListener('resize', this.handleResize);
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.wrapper.detach();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }

  hasMetaValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) {
      return;
    }

    const chartConfig = this.getChartConfig();
    const chartHeight =
      this.data?.chartHeight ||
      this.data?.height ||
      chartConfig.chartHeight ||
      chartConfig.height ||
      'var(--dvm-chart-height-lg, 400px)';
    chartDom.style.height = String(chartHeight);

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    if (this.data?.data) {
      this.renderChart(this.data.data);
      return;
    }

    if (!this.data?.apiUrl) {
      this.lastRawPayload = undefined;
      this.showNoDataMessage();
      return;
    }

    this.configService.getConfig(this.data.apiUrl, this.data, filters, this.data?.method).subscribe({
      next: (response: any) => {
        this.applyRemoteWidgetOverrides(response);
        this.renderChart(response);
      },
      error: () => {
        this.lastRawPayload = undefined;
        this.showNoDataMessage();
      },
    });
  }

  protected renderChart(rawPayload: any): void {
    this.lastRawPayload = rawPayload;
    this.currentTheme = this.themeService.resolve(this.data?.themeOverrides);
    const normalized = this.normalizeChartData(rawPayload);
    this.metaInfo = normalized.metaInfo;

    if (!normalized.categories.length) {
      this.showNoDataMessage();
      return;
    }

    const chartConfig = this.getChartConfig();
    const fontScale = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.max(1, Math.round(px * fontScale));
    const palette: string[] =
      chartConfig.colors ||
      this.data?.colors ||
      this.getDefaultPalette();

    const seriesConfigs = this.resolveSeriesConfigs(chartConfig, palette);
    if (!seriesConfigs.length) {
      this.showNoDataMessage();
      return;
    }

    this.allCategories = normalized.categories;
    this.allSeriesNames = seriesConfigs.map((seriesConfig) => seriesConfig.name);
    this.wrapper.setSourceData(
      this.buildWrapperSourceRows(normalized),
      '__comboCategory',
      '__comboQuarter',
      '__comboYear',
      ...this.resolveCategoryFields(chartConfig),
    );

    const sharedYAxis = (chartConfig.sharedYAxis ?? chartConfig.useSharedYAxis ?? this.data?.sharedYAxis) === true;
    const axisCount = sharedYAxis
      ? 1
      : Math.max(
          1,
          chartConfig.yAxes?.length || 0,
          ...seriesConfigs.map((seriesConfig) => Number(seriesConfig.yAxisIndex ?? 0) + 1),
        );
    const yAxisOptions = this.buildYAxisOptions(chartConfig, seriesConfigs, axisCount, sharedYAxis, sc);
    const tooltipMetaBySeries = new Map<string, TooltipSeriesMeta>();

    const categories = normalized.categories;
    const isLargeDataset =
      categories.length * Math.max(seriesConfigs.length, 1) >=
      Number(chartConfig.largeDataThreshold ?? this.data?.largeDataThreshold ?? 600);
    const enableDataZoom =
      (chartConfig.enableDataZoom ?? this.data?.enableDataZoom) === true ||
      categories.length >= Number(chartConfig.autoDataZoomThreshold ?? this.data?.autoDataZoomThreshold ?? 12);
    const hideDenseLabels =
      (chartConfig.autoHideLabels ?? this.data?.autoHideLabels ?? true) === true &&
      categories.length > Number(chartConfig.labelDensityThreshold ?? this.data?.labelDensityThreshold ?? 20);
    const legendSelected = this.buildLegendSelectedState(seriesConfigs);
    const tooltipExtraFields = Array.isArray(chartConfig?.tooltip?.fields)
      ? (chartConfig.tooltip.fields as TooltipFieldConfig[])
      : [];

    const series = seriesConfigs.map((seriesConfig, index) =>
      this.buildSeriesOption(
        seriesConfig,
        normalized,
        chartConfig,
        yAxisOptions.length,
        palette[index % palette.length],
        hideDenseLabels,
        isLargeDataset,
        sc,
        tooltipMetaBySeries,
      ),
    );

    const annotatedSeries = this.applyAnnotations(series, seriesConfigs, normalized, chartConfig, sc);
    const quarterYearAxis = this.shouldUseQuarterYearAxis(chartConfig, normalized);
    const xAxisLabelInterval = chartConfig.xAxisLabelInterval ?? chartConfig.xAxis?.axisLabel?.interval ?? (isLargeDataset ? 'auto' : 0);
    const tooltipAxisPointer = chartConfig?.tooltip?.axisPointerType || 'shadow';

    const option: echarts.EChartsOption = {
      animation:
        !isLargeDataset ||
        (chartConfig.disableAnimationForLargeData ?? this.data?.disableAnimationForLargeData ?? true) === false,
      animationThreshold: Number(chartConfig.animationThreshold ?? this.data?.animationThreshold ?? 2000),
      color: palette,
      legend: {
        show: chartConfig.showLegend !== false,
        type: chartConfig.legendScroll === false ? 'plain' : 'scroll',
        top: chartConfig.legendTop ?? sc(6),
        left: chartConfig.legendLeft ?? 'center',
        right: chartConfig.legendRight,
        bottom: chartConfig.legendBottom,
        orient: chartConfig.legendOrient ?? 'horizontal',
        width: this.scaleOptionalDimension(chartConfig.legendWidth ?? '72%', sc),
        height: this.scaleOptionalDimension(chartConfig.legendHeight ?? 24, sc),
        selectedMode: true,
        data: this.allSeriesNames,
        selected: legendSelected,
        textStyle: {
          fontSize: sc(Number(chartConfig.legendFontSize ?? 11)),
          color: this.resolveThemeManagedColor(
            chartConfig.legendTextColor,
            this.currentTheme?.chart?.legendTextColor ?? this.currentTheme?.chart?.textColor,
            ['#334155', '#475569', '#64748b', '#0f172a', '#0a0a0a', '#000', '#000000'],
          ) ?? '#334155',
          fontWeight: chartConfig.legendFontWeight,
        },
        itemWidth: sc(Number(chartConfig.legendItemWidth ?? 14)),
        itemHeight: sc(Number(chartConfig.legendItemHeight ?? 10)),
        itemGap: sc(Number(chartConfig.legendItemGap ?? 10)),
        pageButtonPosition: chartConfig.legendPageButtonPosition ?? 'end',
        pageButtonGap: sc(Number(chartConfig.legendPageButtonGap ?? 6)),
        pageIconSize: sc(Number(chartConfig.legendPageIconSize ?? 11)),
        pageTextStyle: {
          color: this.resolveThemeManagedColor(
            chartConfig.legendPageTextColor,
            this.currentTheme?.chart?.axisLabelColor,
            ['#334155', '#475569', '#64748b', '#0f172a', '#0a0a0a', '#000', '#000000'],
          ) ?? '#64748b',
          fontSize: sc(Number(chartConfig.legendPageFontSize ?? 10)),
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: tooltipAxisPointer },
        appendToBody: chartConfig?.tooltip?.appendToBody ?? true,
        confine: chartConfig?.tooltip?.confine ?? false,
        backgroundColor:
          this.resolveThemeManagedColor(
            chartConfig?.tooltip?.backgroundColor,
            this.currentTheme?.tooltip?.bg,
            ['rgba(255,255,255,0.96)', 'rgba(255,255,255,0.97)', '#ffffff', '#fff', 'white'],
          ) ?? 'rgba(255, 255, 255, 0.96)',
        borderColor:
          this.resolveThemeManagedColor(
            chartConfig?.tooltip?.borderColor,
            this.currentTheme?.tooltip?.borderColor,
            ['#e2e8f0', '#cbd5e1', 'rgba(0,0,0,0.2)'],
          ) ?? '#e2e8f0',
        borderWidth: Number(chartConfig?.tooltip?.borderWidth ?? 1),
        extraCssText:
          chartConfig?.tooltip?.extraCssText ??
          `box-shadow:${this.currentTheme?.card?.hoverShadow || this.currentTheme?.card?.shadow || '0 12px 30px rgba(15,23,42,0.14)'};border-radius:8px;`,
        textStyle: {
          fontSize: sc(Number(chartConfig?.tooltip?.fontSize ?? this.currentTheme?.tooltip?.fontSize ?? 12)),
          color:
            this.resolveThemeManagedColor(
              chartConfig?.tooltip?.textColor,
              this.currentTheme?.tooltip?.textColor,
              ['#334155', '#475569', '#64748b', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
            ) ?? '#0f172a',
        },
        formatter: (params: any) =>
          this.buildTooltipContent(params, normalized, tooltipMetaBySeries, tooltipExtraFields),
      },
      grid: {
        top: sc(chartConfig.gridTop ?? 62),
        left: sc(chartConfig.gridLeft ?? 36),
        right: sc(chartConfig.gridRight ?? (yAxisOptions.length > 1 ? 54 : 34)),
        bottom: sc(chartConfig.gridBottom ?? (quarterYearAxis ? (enableDataZoom ? 104 : 84) : enableDataZoom ? 78 : 58)),
        containLabel: true,
      },
      dataZoom: enableDataZoom
        ? [
            {
              type: 'inside',
              xAxisIndex: [0],
              start: Number(chartConfig.dataZoomStart ?? 0),
              end: Number(chartConfig.dataZoomEnd ?? 100),
            },
            {
              type: 'slider',
              xAxisIndex: [0],
              height: sc(Number(chartConfig.dataZoomHeight ?? 14)),
              bottom: sc(Number(chartConfig.dataZoomBottom ?? 10)),
              start: Number(chartConfig.dataZoomStart ?? 0),
              end: Number(chartConfig.dataZoomEnd ?? 100),
              borderColor: chartConfig.dataZoomBorderColor ?? this.currentTheme?.chart?.axisLineColor ?? '#cbd5e1',
              fillerColor: chartConfig.dataZoomFillerColor ?? 'rgba(37, 99, 235, 0.14)',
              handleStyle: {
                color: chartConfig.dataZoomHandleColor ?? palette[0] ?? '#2563eb',
              },
            },
          ]
        : undefined,
      xAxis: quarterYearAxis
        ? [
            {
              type: 'category',
              data: categories.map((category) => normalized.categoryMetaByCategory[category]?.quarter || category),
              triggerEvent: true,
              axisTick: { alignWithLabel: true },
              axisLabel: this.buildXAxisLabelOptions(chartConfig, sc, xAxisLabelInterval),
            },
            {
              type: 'category',
              position: 'bottom',
              offset: sc(chartConfig.yearAxisOffset ?? 22),
              data: this.buildYearAxisValues(categories, normalized),
              triggerEvent: true,
              axisTick: { show: false },
              axisLine: { show: false },
              axisLabel: this.buildXAxisLabelOptions(chartConfig, sc, xAxisLabelInterval, true),
            },
          ]
        : {
            type: 'category',
            data: categories,
            triggerEvent: true,
            axisTick: { alignWithLabel: true },
            axisLabel: this.buildXAxisLabelOptions(chartConfig, sc, xAxisLabelInterval),
          },
      yAxis: yAxisOptions as any,
      series: annotatedSeries as any,
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.wrapper.afterRender();

    if (this.selectedLegends.length > 0) {
      this.highlightSeriesByNames(this.selectedLegends);
    }
  }

  protected getChartConfig(): any {
    return this.data?.chartConfig || {};
  }

  protected applyRemoteWidgetOverrides(response: any): void {
    if (!response || typeof response !== 'object') {
      return;
    }

    const hasWidgetShape =
      response.chartConfig !== undefined ||
      response.type !== undefined ||
      response.title !== undefined ||
      response.subtitle !== undefined;

    if (!hasWidgetShape) {
      return;
    }

    const { data: _responseRows, ...widgetOverrides } = response;

    this.data = {
      ...this.data,
      ...widgetOverrides,
      widgetId: this.data?.widgetId || response.widgetId,
      apiUrl: this.data?.apiUrl,
    };
  }

  protected resolveSeriesConfigs(chartConfig: any, palette: string[]): ComboSeriesConfig[] {
    const resolvedPalette =
      palette.length > 0
        ? palette
        : (this.getChartConfig().colors ||
            this.data?.colors ||
            this.getDefaultPalette());
    const rootSeries = Array.isArray(this.data?.series) ? this.data.series : [];
    const explicitSeries = Array.isArray(chartConfig.series) && chartConfig.series.length
      ? chartConfig.series
      : rootSeries;

    if (explicitSeries.length) {
      return explicitSeries
        .map((seriesConfig: any, index: number) =>
          this.normalizeSeriesConfig(
            seriesConfig,
            seriesConfig?.mode,
            resolvedPalette[index % resolvedPalette.length],
            index,
            chartConfig,
          ),
        )
        .filter((seriesConfig: ComboSeriesConfig | null): seriesConfig is ComboSeriesConfig => Boolean(seriesConfig));
    }

    const barSeries: Array<ComboSeriesConfig | null> = (chartConfig.barSeries || this.data?.barSeries || []).map(
      (seriesConfig: any, index: number) =>
        this.normalizeSeriesConfig(
          seriesConfig,
          'bar',
          resolvedPalette[index % resolvedPalette.length],
          index,
          chartConfig,
        ),
    );
    const lineSeries: Array<ComboSeriesConfig | null> = (chartConfig.lineSeries || this.data?.lineSeries || []).map(
      (seriesConfig: any, index: number) =>
        this.normalizeSeriesConfig(
          seriesConfig,
          'line',
          resolvedPalette[(barSeries.length + index) % resolvedPalette.length],
          barSeries.length + index,
          chartConfig,
        ),
    );

    return [...barSeries, ...lineSeries].filter(
      (seriesConfig: ComboSeriesConfig | null): seriesConfig is ComboSeriesConfig => Boolean(seriesConfig),
    );
  }

  protected normalizeSeriesConfig(
    source: any,
    fallbackMode: ComboSeriesMode | undefined,
    fallbackColor: string,
    fallbackIndex: number,
    chartConfig: any,
  ): ComboSeriesConfig | null {
    if (!source) {
      return null;
    }

    if (typeof source === 'string') {
      const mode = fallbackMode || (fallbackIndex === 0 ? 'bar' : 'line');
      return {
        mode,
        name: this.prettifyLabel(source),
        valueField: source,
        color: fallbackColor,
        yAxisIndex: mode === 'line' ? 1 : 0,
      };
    }

    const mode = (source.mode || fallbackMode || 'bar') as ComboSeriesMode;
    const valueField = source.valueField || source.field || source.dataKey;
    if (!valueField) {
      return null;
    }

    return {
      mode,
      name: String(source.name || source.label || this.prettifyLabel(valueField)),
      valueField: String(valueField),
      dimensionField: source.dimensionField || source.groupField || '__all__',
      dimensionValue: source.dimensionValue ?? source.groupValue ?? '__all__',
      yAxisIndex: Number(source.yAxisIndex ?? (mode === 'line' ? 1 : 0)),
      color: source.color || fallbackColor,
      prefix: source.prefix ?? (mode === 'line' ? chartConfig.linePrefix : chartConfig.barPrefix),
      valueSuffix:
        source.valueSuffix ??
        source.suffix ??
        (mode === 'line' ? chartConfig.lineSuffix ?? this.data?.lineSuffix : chartConfig.barSuffix ?? this.data?.barSuffix),
      valueDecimals: Number(
        source.valueDecimals ??
          source.decimals ??
          (mode === 'line' ? chartConfig.lineDecimals ?? this.data?.lineDecimals : chartConfig.barDecimals ?? this.data?.barDecimals) ??
          0,
      ),
      valueScale: Number(
        source.valueScale ??
          source.scale ??
          (mode === 'line' ? chartConfig.lineScale ?? this.data?.lineScale : chartConfig.barScale ?? this.data?.barScale) ??
          1,
      ),
      showLabel: source.showLabel,
      labelPosition: source.labelPosition,
      labelDistance: source.labelDistance,
      labelOffset: source.labelOffset,
      labelColor: source.labelColor,
      labelFontSize: source.labelFontSize,
      labelFontWeight: source.labelFontWeight,
      labelLayout: source.labelLayout,
      labelBandRow: source.labelBandRow,
      stack: source.stack,
      barWidth: source.barWidth,
      barMaxWidth: source.barMaxWidth,
      barMinHeight: source.barMinHeight,
      lineStyleType: source.lineStyleType ?? source.lineStyle ?? 'solid',
      lineWidth: Number(source.lineWidth ?? 2),
      lineSmooth: source.lineSmooth ?? false,
      lineSymbol: source.lineSymbol,
      lineSymbolSize: Number(source.lineSymbolSize ?? 6),
      showSymbol: source.showSymbol,
      connectNulls: source.connectNulls,
      sampling: source.sampling,
      hidden: source.hidden,
      visible: source.visible,
      initiallyHidden: source.initiallyHidden,
    };
  }

  protected normalizeChartData(rawPayload: any): NormalizedComboData {
    const chartConfig = this.getChartConfig();
    const rows = this.resolveRows(rawPayload);
    const categoryFields = this.resolveCategoryFields(chartConfig);
    const categoryDelimiter = String(chartConfig.categoryDelimiter ?? this.data?.categoryDelimiter ?? ' ');
    const quarterField = String(chartConfig.quarterField || this.data?.quarterField || 'quarter');
    const yearField = String(chartConfig.yearField || this.data?.yearField || 'year');

    const rowsByCategory = new Map<string, any[]>();
    const primaryRowByCategory: Record<string, any> = {};
    const categoryMetaByCategory: Record<string, CategoryMetaInfo> = {};

    rows.forEach((row: any) => {
      const category = categoryFields
        .map((field) => String(this.resolvePath(row, field) ?? ''))
        .join(categoryDelimiter)
        .trim();
      if (!category) {
        return;
      }

      if (!rowsByCategory.has(category)) {
        rowsByCategory.set(category, []);
      }
      rowsByCategory.get(category)?.push(row);
      primaryRowByCategory[category] = primaryRowByCategory[category] || row;
      categoryMetaByCategory[category] = {
        quarter: this.resolvePath(row, quarterField) ?? undefined,
        year: this.resolvePath(row, yearField) ?? undefined,
      };
    });

    const categories = Array.from(rowsByCategory.keys());
    this.sortCategories(categories, primaryRowByCategory, categoryMetaByCategory, chartConfig);

    return {
      categories,
      rowsByCategory: Object.fromEntries(categories.map((category) => [category, rowsByCategory.get(category) || []])),
      primaryRowByCategory,
      categoryMetaByCategory,
      metaInfo: this.resolveMetaInfo(rawPayload, rows),
    };
  }

  protected resolveRows(rawPayload: any): any[] {
    const chartConfig = this.getChartConfig();
    const payload = rawPayload?.data ?? rawPayload;
    const dataKey = this.data?.dataKey || chartConfig.dataKey;

    const keyedRows = dataKey
      ? this.resolvePath(payload, dataKey) ?? this.resolvePath(rawPayload, dataKey)
      : undefined;

    if (Array.isArray(keyedRows)) {
      return keyedRows;
    }
    if (Array.isArray(payload)) {
      return payload;
    }
    if (Array.isArray(payload?.data)) {
      return payload.data;
    }
    if (Array.isArray(rawPayload?.data)) {
      return rawPayload.data;
    }
    return [];
  }

  protected resolveCategoryFields(chartConfig: any): string[] {
    const configuredFields = chartConfig.categoryFields || this.data?.categoryFields;
    if (Array.isArray(configuredFields) && configuredFields.length > 0) {
      return configuredFields.map((field: unknown) => String(field));
    }

    const categoryField = chartConfig.categoryField || this.data?.categoryField || 'category';
    return [String(categoryField)];
  }

  protected resolveMetaInfo(rawPayload: any, rows: any[]): NormalizedComboData['metaInfo'] {
    const source = rawPayload?.data && !Array.isArray(rawPayload.data) ? rawPayload.data : rawPayload;

    return {
      totalRecords: Number(source?.totalRecords ?? rows.length),
      totalPages: Number(source?.totalPages),
      currentPage: Number(source?.currentPage),
      pageSize: Number(source?.pageSize),
    };
  }

  protected buildWrapperSourceRows(normalized: NormalizedComboData): any[] {
    return normalized.categories.flatMap((category) =>
      (normalized.rowsByCategory[category] || []).map((row) => ({
        ...row,
        __comboCategory: category,
        __comboQuarter: normalized.categoryMetaByCategory[category]?.quarter ?? null,
        __comboYear: normalized.categoryMetaByCategory[category]?.year ?? null,
      })),
    );
  }

  protected sortCategories(
    categories: string[],
    primaryRowByCategory: Record<string, any>,
    categoryMetaByCategory: Record<string, CategoryMetaInfo>,
    chartConfig: any,
  ): void {
    const sortMode = String(chartConfig.categorySort ?? this.data?.categorySort ?? 'auto').toLowerCase();
    const quarterRank: Record<string, number> = { q1: 1, q2: 2, q3: 3, q4: 4 };

    categories.sort((left, right) => {
      if (sortMode === 'none') {
        return 0;
      }

      if (sortMode === 'asc') {
        return left.localeCompare(right, undefined, { numeric: true });
      }

      if (sortMode === 'desc') {
        return right.localeCompare(left, undefined, { numeric: true });
      }

      const leftMeta = categoryMetaByCategory[left];
      const rightMeta = categoryMetaByCategory[right];
      const leftYear = Number(leftMeta?.year);
      const rightYear = Number(rightMeta?.year);
      const leftQuarter = quarterRank[String(leftMeta?.quarter || '').toLowerCase()] ?? 0;
      const rightQuarter = quarterRank[String(rightMeta?.quarter || '').toLowerCase()] ?? 0;

      if (Number.isFinite(leftYear) && Number.isFinite(rightYear) && (leftYear !== rightYear || leftQuarter !== rightQuarter)) {
        return leftYear === rightYear ? leftQuarter - rightQuarter : leftYear - rightYear;
      }

      if (sortMode === 'time-asc' || sortMode === 'time-desc') {
        const leftRow = primaryRowByCategory[left];
        const rightRow = primaryRowByCategory[right];
        const leftTime = Number(new Date(leftRow?.date ?? leftRow?.timestamp ?? left).getTime());
        const rightTime = Number(new Date(rightRow?.date ?? rightRow?.timestamp ?? right).getTime());
        if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
          return sortMode === 'time-desc' ? rightTime - leftTime : leftTime - rightTime;
        }
      }

      return left.localeCompare(right, undefined, { numeric: true });
    });
  }

  protected buildYAxisOptions(
    chartConfig: any,
    seriesConfigs: ComboSeriesConfig[],
    axisCount: number,
    sharedYAxis: boolean,
    sc: (px: number) => number,
  ): echarts.YAXisComponentOption[] {
    const configuredAxes = Array.isArray(chartConfig.yAxes) ? chartConfig.yAxes : [];
    const defaultsByAxis = new Map<number, ComboYAxisConfig>();

    seriesConfigs.forEach((seriesConfig) => {
      const axisIndex = sharedYAxis ? 0 : Number(seriesConfig.yAxisIndex ?? 0);
      if (defaultsByAxis.has(axisIndex)) {
        return;
      }
      defaultsByAxis.set(axisIndex, {
        suffix: seriesConfig.valueSuffix,
        prefix: seriesConfig.prefix,
        decimals: seriesConfig.valueDecimals,
        position: axisIndex % 2 === 0 ? 'left' : 'right',
      });
    });

    return Array.from({ length: axisCount }, (_, axisIndex) => {
      const configuredAxis = configuredAxes[axisIndex] || {};
      const defaultAxis = defaultsByAxis.get(axisIndex) || {};
      const axisConfig: ComboYAxisConfig = {
        ...defaultAxis,
        ...configuredAxis,
        position: configuredAxis.position || defaultAxis.position || (axisIndex % 2 === 0 ? 'left' : 'right'),
      };

      const configurableLabel = buildConfigurableAxisLabel(axisConfig as any);
      const decimals = Number(axisConfig.decimals ?? 0);
      const prefix = String(axisConfig.prefix || '');
      const suffix = String(axisConfig.suffix || '');

      return {
        type: 'value',
        position: axisConfig.position,
        min: axisConfig.min,
        max: axisConfig.max,
        interval: axisConfig.interval,
        name: axisConfig.name,
        nameGap: sc(Number(axisConfig.nameGap ?? chartConfig.yAxisNameGap ?? 32)),
        nameTextStyle: {
          fontSize: sc(Number(axisConfig.nameFontSize ?? chartConfig.yAxisNameFontSize ?? 11)),
          color:
            this.resolveThemeManagedColor(
              axisConfig.nameColor ?? chartConfig.yAxisNameColor,
              this.currentTheme?.chart?.textColor ?? this.currentTheme?.chart?.axisLabelColor,
              ['#334155', '#475569', '#64748b', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
            ) ?? '#334155',
          fontWeight: chartConfig.yAxisNameFontWeight,
        },
        splitLine: {
          show: axisConfig.splitLine ?? axisIndex === 0,
          lineStyle: {
            color:
              this.resolveThemeManagedColor(
                axisConfig.splitLineColor ?? chartConfig.yAxisSplitLineColor,
                this.currentTheme?.chart?.splitLineColor,
                ['#e2e8f0', '#cbd5e1', '#f3f4f6', 'rgba(0,0,0,0.2)'],
              ) ?? '#e2e8f0',
            type: axisConfig.splitLineType ?? chartConfig.yAxisSplitLineType ?? 'dashed',
            width: Number(chartConfig.yAxisSplitLineWidth ?? 1),
          },
        },
        axisLabel: {
          show: axisConfig.show !== false,
          fontSize: sc(Number(axisConfig.labelFontSize ?? chartConfig.yAxisLabelFontSize ?? 11)),
          color:
            this.resolveThemeManagedColor(
              axisConfig.labelColor ?? chartConfig.yAxisLabelColor,
              this.currentTheme?.chart?.axisLabelColor,
              ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
            ) ?? '#475569',
          hideOverlap: chartConfig.yAxisLabelHideOverlap ?? true,
          formatter:
            configurableLabel?.formatter ||
            ((value: number) => this.formatScaledValue(Number(value), decimals, suffix, prefix)),
          ...(configurableLabel?.show === false ? { show: false } : {}),
        },
      } as echarts.YAXisComponentOption;
    });
  }

  protected buildSeriesOption(
    seriesConfig: ComboSeriesConfig,
    normalized: NormalizedComboData,
    chartConfig: any,
    axisCount: number,
    fallbackColor: string,
    hideDenseLabels: boolean,
    isLargeDataset: boolean,
    sc: (px: number) => number,
    tooltipMetaBySeries: Map<string, TooltipSeriesMeta>,
  ): echarts.SeriesOption {
    const sharedYAxis = (chartConfig.sharedYAxis ?? chartConfig.useSharedYAxis ?? this.data?.sharedYAxis) === true;
    const axisIndex = this.clampAxisIndex(sharedYAxis ? 0 : seriesConfig.yAxisIndex, axisCount);
    const prefix = String(seriesConfig.prefix || '');
    const suffix = String(seriesConfig.valueSuffix ?? '');
    const decimals = Number(seriesConfig.valueDecimals ?? 0);
    const scale = this.getSafeScale(seriesConfig.valueScale);
    const baseColor = seriesConfig.color || fallbackColor;
    const color = this.wrapper.applyColorConsistency(seriesConfig.name, baseColor) || baseColor;
    const categories = normalized.categories;
    const globalShowDataLabels = chartConfig.showDataLabels ?? this.data?.showDataLabels;
    const globalShowLinePointValues = chartConfig.showLinePointValues ?? this.data?.showLinePointValues;
    const showLabel =
      seriesConfig.showLabel ??
      (seriesConfig.mode === 'line'
        ? globalShowLinePointValues !== false && chartConfig.showLineLabels !== false
        : globalShowDataLabels !== undefined
          ? globalShowDataLabels === true
          : chartConfig.showBarLabels === true);

    tooltipMetaBySeries.set(seriesConfig.name, {
      mode: seriesConfig.mode,
      prefix,
      suffix,
      decimals,
      scale,
    });

    const dataPoints = categories.map((category) => {
      const rows = normalized.rowsByCategory[category] || [];
      const row = this.findSeriesRow(rows, seriesConfig);
      const rawValue = row ? Number(this.resolvePath(row, seriesConfig.valueField)) : Number.NaN;
      const hasNumericValue = Number.isFinite(rawValue);
      const scaledValue = hasNumericValue ? rawValue / scale : null;

      return {
        value: scaledValue,
        rawValue: hasNumericValue ? rawValue : null,
        category,
        quarter: normalized.categoryMetaByCategory[category]?.quarter,
        year: normalized.categoryMetaByCategory[category]?.year,
        row,
      };
    });

    if (seriesConfig.mode === 'bar') {
      return {
        name: seriesConfig.name,
        type: 'bar',
        yAxisIndex: axisIndex,
        xAxisIndex: 0,
        stack: seriesConfig.stack,
        barWidth: seriesConfig.barWidth ?? chartConfig.barWidth,
        barMaxWidth: seriesConfig.barMaxWidth ?? sc(chartConfig.barMaxWidth ?? 26),
        barMinHeight: seriesConfig.barMinHeight ?? chartConfig.barMinHeight,
        barGap: chartConfig.barGap,
        barCategoryGap: chartConfig.barCategoryGap ?? '28%',
        large: categories.length >= Number(chartConfig.largeBarThreshold ?? this.data?.largeBarThreshold ?? 80),
        largeThreshold: Number(chartConfig.largeBarThreshold ?? this.data?.largeBarThreshold ?? 80),
        progressive: Number(chartConfig.progressive ?? this.data?.progressive ?? 3000),
        progressiveThreshold: Number(chartConfig.progressiveThreshold ?? this.data?.progressiveThreshold ?? 5000),
        itemStyle: {
          color,
          borderRadius: Number(chartConfig.barBorderRadius ?? 4),
        },
        emphasis: {
          focus: 'series',
        },
        label:
          !showLabel || hideDenseLabels
            ? { show: false }
            : {
                show: true,
                position: seriesConfig.labelPosition || chartConfig.barLabelPosition || chartConfig.labelPosition || 'top',
                distance: sc(Number(seriesConfig.labelDistance ?? chartConfig.barLabelDistance ?? chartConfig.labelDistance ?? 4)),
                offset: this.scaleLabelOffset(seriesConfig.labelOffset ?? chartConfig.barLabelOffset ?? chartConfig.labelOffset, sc),
                fontSize: sc(Number(seriesConfig.labelFontSize ?? chartConfig.barLabelFontSize ?? chartConfig.labelFontSize ?? 10)),
                color:
                  this.resolveThemeManagedColor(
                    seriesConfig.labelColor ?? chartConfig.barLabelColor ?? chartConfig.labelColor,
                    this.currentTheme?.chart?.axisLabelColor,
                    ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
                  ) ?? '#475569',
                fontWeight: seriesConfig.labelFontWeight ?? chartConfig.barLabelFontWeight ?? chartConfig.labelFontWeight ?? 600,
                textBorderColor:
                  this.resolveThemeManagedColor(
                    chartConfig.labelTextBorderColor,
                    this.currentTheme?.card?.bodyBg ?? this.currentTheme?.layout?.content?.bg,
                    ['#ffffff', '#fff', 'white'],
                  ) ?? '#ffffff',
                textBorderWidth: Number(chartConfig.labelTextBorderWidth ?? 2),
                formatter: (params: any) =>
                  this.formatDisplayValue(
                    Number(params?.data?.rawValue ?? 0),
                    scale,
                    decimals,
                    suffix,
                    prefix,
                  ),
              },
        labelLayout: this.buildLabelLayout(seriesConfig, chartConfig, 'bar', sc),
        data: dataPoints,
      };
    }

    return {
      name: seriesConfig.name,
      type: 'line',
      yAxisIndex: axisIndex,
      xAxisIndex: 0,
      symbol: seriesConfig.lineSymbol || chartConfig.lineSymbol || 'circle',
      symbolSize: sc(seriesConfig.lineSymbolSize ?? chartConfig.lineSymbolSize ?? 6),
      showSymbol: seriesConfig.showSymbol ?? chartConfig.showLineMarkers ?? true,
      connectNulls: seriesConfig.connectNulls ?? chartConfig.connectLineNulls ?? false,
      smooth: seriesConfig.lineSmooth ?? chartConfig.lineSmooth ?? false,
      sampling: (seriesConfig.sampling || chartConfig.lineSampling || (isLargeDataset ? 'lttb' : undefined)) as any,
      progressive: Number(chartConfig.progressive ?? this.data?.progressive ?? 3000),
      progressiveThreshold: Number(chartConfig.progressiveThreshold ?? this.data?.progressiveThreshold ?? 5000),
      itemStyle: {
        color,
      },
      lineStyle: {
        color,
        width: sc(seriesConfig.lineWidth ?? chartConfig.lineWidth ?? 2),
        type: seriesConfig.lineStyleType || chartConfig.lineStyleType || 'solid',
      },
      emphasis: {
        focus: 'series',
      },
      label:
        !showLabel || hideDenseLabels
          ? { show: false }
          : {
              show: true,
              position: seriesConfig.labelPosition || chartConfig.lineLabelPosition || chartConfig.labelPosition || 'top',
              distance: sc(Number(seriesConfig.labelDistance ?? chartConfig.lineLabelDistance ?? chartConfig.labelDistance ?? 8)),
              offset: this.scaleLabelOffset(seriesConfig.labelOffset ?? chartConfig.lineLabelOffset ?? chartConfig.labelOffset, sc),
              fontSize: sc(Number(seriesConfig.labelFontSize ?? chartConfig.lineLabelFontSize ?? chartConfig.labelFontSize ?? 10)),
              color:
                this.resolveThemeManagedColor(
                  seriesConfig.labelColor ?? chartConfig.lineLabelColor ?? chartConfig.labelColor,
                  this.currentTheme?.chart?.dataLabelOutside ?? color,
                  ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
                ) ?? color,
              fontWeight: seriesConfig.labelFontWeight ?? chartConfig.lineLabelFontWeight ?? chartConfig.labelFontWeight ?? 600,
              textBorderColor:
                this.resolveThemeManagedColor(
                  chartConfig.labelTextBorderColor,
                  this.currentTheme?.card?.bodyBg ?? this.currentTheme?.layout?.content?.bg,
                  ['#ffffff', '#fff', 'white'],
                ) ?? '#ffffff',
              textBorderWidth: Number(chartConfig.labelTextBorderWidth ?? 2),
              formatter: (params: any) =>
                this.formatDisplayValue(
                  Number(params?.data?.rawValue ?? 0),
                  scale,
                  decimals,
                  suffix,
                  prefix,
                ),
            },
      labelLayout: this.buildLabelLayout(seriesConfig, chartConfig, 'line', sc),
      data: dataPoints,
    };
  }

  protected buildXAxisLabelOptions(
    chartConfig: any,
    sc: (px: number) => number,
    interval: string | number,
    yearAxis: boolean = false,
  ): any {
    const axisConfig = chartConfig.xAxis || {};
    const baseLabelConfig = axisConfig.axisLabel || {};
    const yearLabelConfig = chartConfig.yearAxis?.axisLabel || {};
    const labelConfig = yearAxis ? { ...baseLabelConfig, ...yearLabelConfig } : baseLabelConfig;
    const rotate = yearAxis ? 0 : Number(chartConfig.xAxisRotate ?? axisConfig.rotate ?? labelConfig.rotate ?? 0);
    const configuredWidth = yearAxis
      ? chartConfig.yearAxisLabelWidth ?? labelConfig.width
      : chartConfig.xAxisLabelWidth ?? labelConfig.width;

    return {
      ...labelConfig,
      interval: labelConfig.interval ?? interval,
      rotate,
      fontSize: sc(Number(labelConfig.fontSize ?? chartConfig.xAxisLabelFontSize ?? 11)),
      color:
        this.resolveThemeManagedColor(
          labelConfig.color ?? (yearAxis ? chartConfig.yearAxisLabelColor : chartConfig.xAxisLabelColor),
          yearAxis ? this.currentTheme?.chart?.textColor : this.currentTheme?.chart?.axisLabelColor,
          ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
        ) ??
        (yearAxis ? '#334155' : '#475569'),
      fontWeight: labelConfig.fontWeight ?? (yearAxis ? chartConfig.yearAxisFontWeight ?? 600 : chartConfig.xAxisLabelFontWeight),
      hideOverlap: labelConfig.hideOverlap ?? chartConfig.xAxisLabelHideOverlap ?? true,
      margin: sc(Number(labelConfig.margin ?? chartConfig.xAxisLabelMargin ?? (yearAxis ? 14 : 10))),
      overflow: (labelConfig.overflow ?? chartConfig.xAxisLabelOverflow ?? (rotate ? 'truncate' : 'break')) as LabelOverflow,
      width: this.scaleOptionalDimension(configuredWidth ?? (rotate ? 72 : undefined), sc),
      ellipsis: labelConfig.ellipsis ?? chartConfig.xAxisLabelEllipsis ?? '...',
    };
  }

  protected buildLabelLayout(
    seriesConfig: ComboSeriesConfig,
    chartConfig: any,
    mode: ComboSeriesMode,
    sc: (px: number) => number,
  ): any | undefined {
    if ((chartConfig.avoidLabelOverlap ?? true) === false || chartConfig.labelLayout === false) {
      return undefined;
    }

    const modeLayout = mode === 'line' ? chartConfig.lineLabelLayout : chartConfig.barLabelLayout;
    if (modeLayout === false) {
      return undefined;
    }

    const labelLayout = {
      hideOverlap: true,
      ...(mode === 'line' ? { moveOverlap: 'shiftY' } : {}),
      ...(typeof chartConfig.labelLayout === 'object' ? chartConfig.labelLayout : {}),
      ...(typeof modeLayout === 'object' ? modeLayout : {}),
      ...(typeof seriesConfig.labelLayout === 'object' ? seriesConfig.labelLayout : {}),
    };

    const lineLabelPlacement = String(
      seriesConfig.labelLayout?.placement ?? modeLayout?.placement ?? chartConfig.lineLabelPlacement ?? '',
    ).toLowerCase();

    if (mode !== 'line' || lineLabelPlacement !== 'top-band') {
      return labelLayout;
    }

    const bandTop = sc(Number(seriesConfig.labelLayout?.top ?? modeLayout?.top ?? chartConfig.lineLabelBandTop ?? 72));
    const rowGap = sc(Number(seriesConfig.labelLayout?.rowGap ?? modeLayout?.rowGap ?? chartConfig.lineLabelBandRowGap ?? 14));
    const configuredRow = Number(seriesConfig.labelBandRow ?? seriesConfig.labelLayout?.row ?? modeLayout?.row ?? 0);
    const rowIndex = Number.isFinite(configuredRow) ? Math.max(0, configuredRow) : 0;

    return (params: any) => ({
      ...labelLayout,
      x:
        params?.rect?.x !== undefined && params?.rect?.width !== undefined
          ? params.rect.x + params.rect.width / 2
          : params?.labelRect?.x,
      y: bandTop + rowIndex * rowGap,
      align: 'center',
      verticalAlign: 'middle',
    });
  }

  protected scaleLabelOffset(value: unknown, sc: (px: number) => number): [number, number] | undefined {
    if (!Array.isArray(value) || value.length < 2) {
      return undefined;
    }

    const scaleSigned = (input: unknown): number => {
      const numericValue = Number(input ?? 0);
      if (!Number.isFinite(numericValue) || numericValue === 0) {
        return 0;
      }

      const scaled = sc(Math.abs(numericValue));
      return numericValue < 0 ? -scaled : scaled;
    };

    return [scaleSigned(value[0]), scaleSigned(value[1])];
  }

  protected scaleOptionalDimension(value: unknown, sc: (px: number) => number): string | number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'number') {
      return sc(value);
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? sc(numericValue) : String(value);
  }

  protected applyAnnotations(
    series: echarts.SeriesOption[],
    seriesConfigs: ComboSeriesConfig[],
    normalized: NormalizedComboData,
    chartConfig: any,
    sc: (px: number) => number,
  ): echarts.SeriesOption[] {
    const annotations = (chartConfig.annotations || this.data?.annotations || {}) as ComboAnnotationsConfig;
    if (!annotations) {
      return series;
    }

    const bundles = new Map<number, SeriesAnnotationBundle>();
    const defaultLineSeries = seriesConfigs.find((seriesConfig) => seriesConfig.mode === 'line')?.name;
    const firstSeries = seriesConfigs[0]?.name;

    const addToBundle = (targetIndex: number, kind: keyof SeriesAnnotationBundle, item: any) => {
      if (targetIndex < 0) {
        return;
      }
      if (!bundles.has(targetIndex)) {
        bundles.set(targetIndex, {});
      }
      const bundle = bundles.get(targetIndex)!;
      const currentItems = bundle[kind] || [];
      bundle[kind] = [...currentItems, item];
    };

    (annotations.regions || []).forEach((region) => {
      const startIndex = normalized.categories.findIndex((category) => String(category) === String(region.startCategory));
      const endIndex = normalized.categories.findIndex((category) => String(category) === String(region.endCategory));
      const targetIndex = this.resolveTargetSeriesIndex(region.targetSeries || firstSeries, seriesConfigs);
      if (startIndex === -1 || endIndex === -1 || targetIndex === -1) {
        return;
      }

      addToBundle(targetIndex, 'markAreaData', [
        {
          name: region.label || region.name,
          xAxis: Math.min(startIndex, endIndex),
          itemStyle: {
            color: region.color || chartConfig.annotationRegionColor || 'rgba(250, 204, 21, 0.18)',
          },
          label: {
            show: Boolean(region.label || region.name),
            color:
              this.resolveThemeManagedColor(
                chartConfig.annotationLabelColor,
                this.currentTheme?.chart?.axisLabelColor,
                ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
              ) ?? '#475569',
          },
        },
        {
          xAxis: Math.max(startIndex, endIndex),
        },
      ]);
    });

    (annotations.lines || []).forEach((line) => {
      const targetSeriesName = line.targetSeries || defaultLineSeries || firstSeries;
      const targetIndex = this.resolveTargetSeriesIndex(targetSeriesName, seriesConfigs);
      if (targetIndex === -1) {
        return;
      }

      const targetSeries = seriesConfigs[targetIndex];
      const axis = line.axis || 'y';
      const lineStyle = {
        color: line.color || chartConfig.annotationLineColor || this.getDefaultPalette()[1] || '#15803d',
        type: line.style || chartConfig.annotationLineType || 'dashed',
        width: Number(line.width ?? chartConfig.annotationLineWidth ?? 2),
      };

      if (axis === 'x') {
        const categoryIndex = normalized.categories.findIndex(
          (category) => String(category) === String(line.category ?? line.value),
        );
        if (categoryIndex === -1) {
          return;
        }

        addToBundle(targetIndex, 'markLineData', {
          name: line.label || line.name,
          xAxis: categoryIndex,
          lineStyle,
          label: {
            show: Boolean(line.label || line.name),
            formatter: line.label || line.name,
          },
        });
        return;
      }

      const scaledValue = Number(line.value) / this.getSafeScale(targetSeries.valueScale);
      if (!Number.isFinite(scaledValue)) {
        return;
      }

      addToBundle(targetIndex, 'markLineData', {
        name: line.label || line.name,
        yAxis: scaledValue,
        lineStyle,
        label: {
          show: Boolean(line.label || line.name),
          formatter: line.label || line.name || String(line.value),
        },
      });
    });

    (annotations.points || []).forEach((point) => {
      const targetSeriesName = point.targetSeries || defaultLineSeries || firstSeries;
      const targetIndex = this.resolveTargetSeriesIndex(targetSeriesName, seriesConfigs);
      if (targetIndex === -1) {
        return;
      }

      const targetSeries = seriesConfigs[targetIndex];
      const categoryIndex = normalized.categories.findIndex((category) => String(category) === String(point.category));
      if (categoryIndex === -1) {
        return;
      }

      const targetRow = normalized.primaryRowByCategory[point.category];
      const fallbackValue = targetRow ? Number(this.resolvePath(targetRow, targetSeries.valueField)) : Number.NaN;
      const rawValue = Number(point.value ?? fallbackValue);
      const scaledValue = rawValue / this.getSafeScale(targetSeries.valueScale);
      if (!Number.isFinite(scaledValue)) {
        return;
      }

      addToBundle(targetIndex, 'markPointData', {
        name: point.label || point.name,
        coord: [categoryIndex, scaledValue],
        symbol: point.symbol || chartConfig.annotationPointSymbol || 'pin',
        symbolSize: sc(Number(point.size ?? chartConfig.annotationPointSize ?? 26)),
        itemStyle: {
          color: point.color || chartConfig.annotationPointColor || this.getDefaultPalette()[0] || '#1d4ed8',
        },
        label: {
          show: Boolean(point.label || point.name),
          formatter: point.label || point.name,
        },
      });
    });

    return series.map((seriesOption, index) => {
      const bundle = bundles.get(index);
      if (!bundle) {
        return seriesOption;
      }

      const nextSeries: any = { ...seriesOption };

      if (bundle.markAreaData?.length) {
        nextSeries.markArea = {
          silent: true,
          data: bundle.markAreaData,
        };
      }

      if (bundle.markLineData?.length) {
        nextSeries.markLine = {
          silent: true,
          symbol: ['none', 'none'],
          data: bundle.markLineData,
        };
      }

      if (bundle.markPointData?.length) {
        nextSeries.markPoint = {
          symbolKeepAspect: true,
          data: bundle.markPointData,
        };
      }

      return nextSeries;
    });
  }

  protected buildTooltipContent(
    params: any,
    normalized: NormalizedComboData,
    tooltipMetaBySeries: Map<string, TooltipSeriesMeta>,
    extraFields: TooltipFieldConfig[],
  ): string {
    const rows = Array.isArray(params) ? params : [params];
    const category = rows[0]?.data?.category || rows[0]?.axisValueLabel || rows[0]?.name || '';
    const categoryMeta = normalized.categoryMetaByCategory[category];
    const title = categoryMeta?.quarter && categoryMeta?.year
      ? `${categoryMeta.quarter} ${categoryMeta.year}`
      : String(category);

    const seriesLines = rows
      .filter((row: any) => row?.seriesName)
      .map((row: any) => {
        const meta = tooltipMetaBySeries.get(String(row.seriesName));
        const rawValue = row?.data?.rawValue;
        const valueText =
          rawValue === null || rawValue === undefined
            ? '-'
            : this.formatDisplayValue(
                Number(rawValue),
                meta?.scale ?? 1,
                meta?.decimals ?? 0,
                meta?.suffix ?? '',
                meta?.prefix ?? '',
              );
        return `<div>${row.marker} <span style="font-weight:600">${row.seriesName}:</span> ${valueText}</div>`;
      })
      .join('');

    const rawRow = normalized.primaryRowByCategory[category];
    const extraLines = rawRow
      ? extraFields
          .map((field) => {
            const rawValue = field.key.startsWith('_')
              ? field.key === '_category'
                ? category
                : rawRow[field.key]
              : this.resolvePath(rawRow, field.key);
            const label = getTooltipLabel(field, true);
            return `<div><span style="font-weight:600">${label}:</span> ${formatTooltipValue(rawValue, field)}</div>`;
          })
          .join('')
      : '';

    return `
      <div style="line-height:1.8;min-width:180px;">
        <div style="font-weight:700;margin-bottom:4px;">${title}</div>
        ${seriesLines}
        ${extraLines}
      </div>
    `;
  }

  protected shouldUseQuarterYearAxis(chartConfig: any, normalized: NormalizedComboData): boolean {
    if ((chartConfig.enableQuarterYearAxis ?? this.data?.enableQuarterYearAxis) === false) {
      return false;
    }

    return normalized.categories.some((category) => {
      const meta = normalized.categoryMetaByCategory[category];
      return Boolean(meta?.quarter) && Boolean(meta?.year);
    });
  }

  protected buildYearAxisValues(categories: string[], normalized: NormalizedComboData): string[] {
    const values = new Array(categories.length).fill('');

    for (let start = 0; start < categories.length;) {
      const year = normalized.categoryMetaByCategory[categories[start]]?.year || '';
      let end = start;
      while (
        end + 1 < categories.length &&
        normalized.categoryMetaByCategory[categories[end + 1]]?.year === year
      ) {
        end += 1;
      }

      if (year) {
        values[Math.floor((start + end) / 2)] = String(year);
      }
      start = end + 1;
    }

    return values;
  }

  protected applyLegendSelectionFromWrapper(selectedIndices: number[]): void {
    if (!this.myChart) {
      return;
    }

    const selectedNames = selectedIndices
      .map((index) => this.allSeriesNames[index])
      .filter((name): name is string => Boolean(name));
    this.selectedLegends = selectedNames;

    const legendToggleMode = String(
      this.getChartConfig().legendToggleMode || this.data?.legendToggleMode || 'visibility',
    ).toLowerCase();

    if (legendToggleMode === 'highlight') {
      this.myChart.setOption({ legend: { selected: this.buildLegendSelectedState() } }, false);
      if (selectedNames.length === 0) {
        this.resetSeriesHighlight();
      } else {
        this.highlightSeriesByNames(selectedNames);
      }
      return;
    }

    const selectedState = this.buildLegendSelectedState(undefined, selectedNames);
    this.myChart.setOption({ legend: { selected: selectedState } }, false);
    this.resetSeriesHighlight();
  }

  protected buildLegendSelectedState(
    seriesConfigs?: ComboSeriesConfig[],
    selectedNames?: string[],
  ): Record<string, boolean> {
    const configs = seriesConfigs || this.resolveSeriesConfigs(this.getChartConfig(), []);
    const selectedState: Record<string, boolean> = {};

    configs.forEach((seriesConfig) => {
      const isInitiallyVisible =
        seriesConfig.visible !== false && seriesConfig.hidden !== true && seriesConfig.initiallyHidden !== true;
      selectedState[seriesConfig.name] =
        selectedNames && selectedNames.length > 0
          ? selectedNames.includes(seriesConfig.name)
          : isInitiallyVisible;
    });

    if (selectedNames && selectedNames.length === 0) {
      configs.forEach((seriesConfig) => {
        selectedState[seriesConfig.name] = true;
      });
    }

    return selectedState;
  }

  protected highlightCategories(categories: string[]): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series) {
      return;
    }

    const categoryIndices = categories
      .map((category) => this.allCategories.findIndex((item) => String(item) === String(category)))
      .filter((index) => index !== -1);

    const updatedSeries = option.series.map((seriesOption: any) => {
      if (!Array.isArray(seriesOption.data)) {
        return seriesOption;
      }

      return {
        ...seriesOption,
        data: seriesOption.data.map((point: any, index: number) => {
          const isSelected = categoryIndices.includes(index);
          return {
            ...point,
            itemStyle: {
              ...(point?.itemStyle || {}),
              opacity: isSelected ? 1 : 0.08,
            },
            label: {
              ...(point?.label || {}),
              opacity: isSelected ? 1 : 0,
            },
          };
        }),
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected resetCategoryHighlight(): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series) {
      return;
    }

    const updatedSeries = option.series.map((seriesOption: any) => {
      if (!Array.isArray(seriesOption.data)) {
        return seriesOption;
      }

      return {
        ...seriesOption,
        data: seriesOption.data.map((point: any) => ({
          ...point,
          itemStyle: {
            ...(point?.itemStyle || {}),
            opacity: 1,
          },
          label: {
            ...(point?.label || {}),
            opacity: 1,
          },
        })),
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected highlightSeriesByNames(names: string[]): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series) {
      return;
    }

    const updatedSeries = option.series.map((seriesOption: any) => {
      const seriesName = Array.isArray(seriesOption.name) ? seriesOption.name[0] : seriesOption.name;
      const isSelected = names.includes(seriesName);
      const opacity = isSelected ? 1 : 0.14;

      return {
        ...seriesOption,
        itemStyle: {
          ...(Array.isArray(seriesOption.itemStyle) ? seriesOption.itemStyle[0] : seriesOption.itemStyle),
          opacity,
        },
        lineStyle: seriesOption.type === 'line'
          ? {
              ...(Array.isArray(seriesOption.lineStyle) ? seriesOption.lineStyle[0] : seriesOption.lineStyle),
              opacity,
            }
          : seriesOption.lineStyle,
        data: Array.isArray(seriesOption.data)
          ? seriesOption.data.map((point: any) => ({
              ...point,
              itemStyle: {
                ...(point?.itemStyle || {}),
                opacity,
              },
              label: {
                ...(point?.label || {}),
                opacity: isSelected ? 1 : 0,
              },
            }))
          : seriesOption.data,
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected resetSeriesHighlight(): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series) {
      return;
    }

    const updatedSeries = option.series.map((seriesOption: any) => ({
      ...seriesOption,
      itemStyle: {
        ...(Array.isArray(seriesOption.itemStyle) ? seriesOption.itemStyle[0] : seriesOption.itemStyle),
        opacity: 1,
      },
      lineStyle: seriesOption.type === 'line'
        ? {
            ...(Array.isArray(seriesOption.lineStyle) ? seriesOption.lineStyle[0] : seriesOption.lineStyle),
            opacity: 1,
          }
        : seriesOption.lineStyle,
      data: Array.isArray(seriesOption.data)
        ? seriesOption.data.map((point: any) => ({
            ...point,
            itemStyle: {
              ...(point?.itemStyle || {}),
              opacity: 1,
            },
            label: {
              ...(point?.label || {}),
              opacity: 1,
            },
          }))
        : seriesOption.data,
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  protected showNoDataMessage(): void {
    if (!this.myChart) {
      return;
    }

    const chartConfig = this.getChartConfig();
    const theme = this.currentTheme || this.themeService.resolve(this.data?.themeOverrides);
    const noDataText = String(
      this.data?.noDataText || chartConfig.noDataText || 'No data is available. Please try changing filters.',
    );

    this.myChart.off('click');
    this.myChart.clear();
    this.myChart.setOption(
      {
        animation: false,
        xAxis: { show: false, type: 'category', data: [] },
        yAxis: { show: false, type: 'value' },
        series: [],
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          silent: true,
          style: {
            text: noDataText,
            fill:
              this.resolveThemeManagedColor(
                chartConfig.noDataTextColor,
                theme?.chart?.axisLabelColor || theme?.layout?.content?.text,
                ['#334155', '#475569', '#64748b', '#505050', '#0f172a', '#0a0a0a', '#333', '#000', '#000000'],
              ) ?? '#64748b',
            fontSize: Number(chartConfig.noDataFontSize ?? 14),
            fontWeight: chartConfig.noDataFontWeight || 500,
            textAlign: 'center',
            textVerticalAlign: 'middle',
          },
        },
      },
      true,
    );
    this.myChart.hideLoading();
  }

  protected getDefaultPalette(): string[] {
    const themePalette = this.currentTheme?.chart?.palette;
    return Array.isArray(themePalette) && themePalette.length > 0 ? themePalette : this.fallbackPalette;
  }

  protected resolveThemeManagedColor(
    configuredColor: unknown,
    themeColor: string | undefined,
    legacyDefaults: string[],
  ): string | undefined {
    if (typeof configuredColor !== 'string' || !configuredColor.trim()) {
      return themeColor;
    }
    if (!themeColor) {
      return configuredColor;
    }
    const normalizedConfigured = this.normalizeColorToken(configuredColor);
    const normalizedLegacyDefaults = legacyDefaults.map((value) => this.normalizeColorToken(value));
    return normalizedLegacyDefaults.includes(normalizedConfigured) ? themeColor : configuredColor;
  }

  protected normalizeColorToken(value: string): string {
    return value.replace(/\s+/g, '').toLowerCase();
  }

  protected findSeriesRow(rows: any[], seriesConfig: ComboSeriesConfig): any | null {
    if (!rows.length) {
      return null;
    }

    const dimensionField = seriesConfig.dimensionField || '__all__';
    const dimensionValue = seriesConfig.dimensionValue ?? '__all__';

    if (dimensionField === '__all__' || dimensionValue === '__all__') {
      return rows.find((row) => Number.isFinite(Number(this.resolvePath(row, seriesConfig.valueField)))) || rows[0];
    }

    return (
      rows.find((row) => String(this.resolvePath(row, dimensionField)) === String(dimensionValue)) ||
      rows.find((row) => Number.isFinite(Number(this.resolvePath(row, seriesConfig.valueField)))) ||
      null
    );
  }

  protected resolveTargetSeriesIndex(targetSeriesName: string | undefined, seriesConfigs: ComboSeriesConfig[]): number {
    if (!targetSeriesName) {
      return seriesConfigs.length > 0 ? 0 : -1;
    }

    return seriesConfigs.findIndex((seriesConfig) => seriesConfig.name === targetSeriesName);
  }

  protected clampAxisIndex(axisIndex: number | undefined, axisCount: number): number {
    if (!Number.isFinite(Number(axisIndex))) {
      return 0;
    }
    return Math.min(Math.max(Number(axisIndex), 0), Math.max(axisCount - 1, 0));
  }

  protected getSafeScale(scale: number | undefined): number {
    const numericScale = Number(scale ?? 1);
    return Number.isFinite(numericScale) && numericScale !== 0 ? numericScale : 1;
  }

  protected formatDisplayValue(
    rawValue: number,
    scale: number = 1,
    decimals: number = 0,
    suffix: string = '',
    prefix: string = '',
  ): string {
    if (!Number.isFinite(rawValue)) {
      return '-';
    }
    return this.formatScaledValue(rawValue / this.getSafeScale(scale), decimals, suffix, prefix);
  }

  protected formatScaledValue(
    value: number,
    decimals: number = 0,
    suffix: string = '',
    prefix: string = '',
  ): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return `${prefix}${value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  }

  protected prettifyLabel(value: string): string {
    return String(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  protected resolvePath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }
}
